import { CheckIcon, ContentCopyIcon, DeleteIcon, Typography, VerifiedUserIcon } from "@orijinworks/frontend-commons"
import AutoDeleteIcon from '@mui/icons-material/AutoDelete';
import { eventEmitter } from "../../../services/event-emitter-service";
import { EVENTS } from "../../../services/constants";
import { LOCATIONS } from "../../../../constants";
import SmsIcon from '@mui/icons-material/Sms';
import moment from "moment";
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import SchoolIcon from '@mui/icons-material/School';

const getFilter = (filters, columnName) => {
    return filters.find(filter => filter.columnName === columnName);
}

const getAnnouncementStatus = (data) => {
    if (data.isDeleted) {
        return {
            icon: <AutoDeleteIcon sx={{ fontWeight: 'bold', color: data.isShutdown ? 'var(--grey-90)' : "var(--red-main)" }} />,
            text: <Typography sx={{ fontWeight: 'bold', color: data.isShutdown ? 'var(--grey-90)' : "var(--red-main)" }}>Deleted</Typography>,
        };
    }
    return {
        icon: <CheckIcon sx={{ fontWeight: 'bold', color: data.isShutdown ? 'var(--grey-90)' : "var(--green-main)" }} />,
        text: <Typography sx={{ fontWeight: 'bold', color: data.isShutdown ? 'var(--grey-90)' : "var(--green-main)" }}>Sent</Typography>,
    };
}

const getRecipientInfoString = (recipients) => {
    if (recipients.length <= 2) {
        return recipients.map(recipient => recipient.name).join(', ');
    }
    return recipients.slice(0, 2).map(recipient => recipient.name).join(', ') + ' + ' + (recipients.length - 2) + ' more';
}

export const mapResponseToAuditConversationData = (data, onDeleteConversationHandler, onConversationSettingsUpdateHandler, onViewConversationHandler) => ({
    rowData: {
        recipient: getRecipientInfoString(data.recipient),
        createdAt: moment(data.createdAt).format('MM/DD/YYYY hh:mm A'),
        message: data.message
    },
    styles: {
        sender: {
            background: data.isShutdown ? 'var(--grey-20)' : '',
            color: data.isShutdown ? 'var(--grey-90) !important' : '',
        },
        recipient: {
            background: data.isShutdown ? 'var(--grey-20)' : '',
            color: data.isShutdown ? 'var(--grey-90) !important' : '',
        },
        createdAt: {
            background: data.isShutdown ? 'var(--grey-20)' : '',
            color: data.isShutdown ? 'var(--grey-90) !important' : '',
        },
        message: {
            background: data.isShutdown ? 'var(--grey-20)' : '',
            color: data.isShutdown ? 'var(--grey-90) !important' : '',
        },
        isDeleted: {
            background: data.isShutdown ? 'var(--grey-20)' : '',
            color: data.isShutdown ? 'var(--grey-90) !important' : '',
        },
        actions: {
            background: data.isShutdown ? 'var(--grey-20)' : '',
            color: data.isShutdown ? 'var(--grey-90) !important' : '',
        }
    },
    preDefineComponentProps: {
        sender: {
            icon: data.roleName == 'Learner' ? <SchoolIcon sx={{ color: data.isShutdown ? 'var(--grey-90)' : "var(--navy-main)" }}/> : <VerifiedUserIcon sx={{ color: data.isShutdown ? 'var(--grey-90)' : "var(--navy-main)" }} />,
            text: <Typography variant="body1">{data.senderName ? data.senderName : 'N/A'}</Typography>,
        },
        isDeleted: getAnnouncementStatus(data),
        actions: [
            {
                title: 'Go to source',
                onClick: () => {
                    if (onViewConversationHandler) {
                        onViewConversationHandler(data);
                    }
                },
                id: 'source',
                disabled: false,
                icon: <SmsIcon sx={{ color: "var(--navy-main)", fontSize: 24 }} />,
                style: {
                    color: "var(--navy-main)"
                }
            },
            {
                title: 'Delete',
                onClick: () => onDeleteConversationHandler(data),
                id: 'delete',
                disabled: data.isDeleted,
                icon: <DeleteIcon sx={{ color: "var(--red-dark)", fontSize: 24 }} />,
                style: {
                    color: "var(--red-dark)"
                }
            },
            {
                title: data.isShutdown ? 'Enable conversation' : 'Shut down conversation',
                onClick: () => onConversationSettingsUpdateHandler(data),
                id: 'settings-update',
                disabled: false,
                icon: data.isShutdown ? <LockOpenIcon sx={{ color: "var(--navy-main)", fontSize: 24 }} /> : <LockIcon sx={{ color: "var(--navy-main)", fontSize: 24 }} />,
                style: {
                    color: "var(--navy-main)"
                }
            }
        ]
    }
})


export const mapResponseToAuditAnnouncementData = (data, onDeleteAnnouncementHandler, onViewAnnouncementHandler) => ({
    rowData: {
        recipient: getRecipientInfoString(data.membersInfo),
        createdAt: moment(data.createdAt).format('MM/DD/YYYY hh:mm A'),
        subject: data.subject
    },
    preDefineComponentProps: {
        sender: {
            icon: <VerifiedUserIcon sx={{ color: "var(--navy-main)" }} />,
            text: <Typography variant="body1">{data.senderName ? data.senderName : 'Unknown Admin'}</Typography>,
        },
        isDeleted: getAnnouncementStatus(data),
        actions: [
            {
                title: 'Go to source',
                onClick: () => {
                    if (onViewAnnouncementHandler) {
                        onViewAnnouncementHandler(data);
                    }
                },
                id: 'source',
                disabled: false,
                icon: <SmsIcon sx={{ color: "var(--navy-main)", fontSize: 24 }} />,
            },
            {
                title: 'Delete',
                onClick: () => onDeleteAnnouncementHandler(data),
                id: 'delete',
                disabled: data.isDeleted,
                icon: <DeleteIcon sx={{ color: "var(--red-dark)", fontSize: 24 }} />,
                style: {
                    color: "var(--red-dark)"
                }
            }
        ]
    }
})

export const getLocationTransformedFilter = (filters) => {
    const filter = filters.find(filter => filter.columnName === 'location');
    if (filter) {
        const locationNames = filter.value.split(",").map(item => item.trim());
        const facilities = LOCATIONS.filter(item => locationNames.includes(item.locationName) && item.locationTypeId === 3).map(item => item.locationId);
        const HUs = LOCATIONS.filter(item => locationNames.includes(item.locationName) && item.locationTypeId === 15).map(item => item.locationId);
        return {
            facilities,
            HUs,
        };    
    }
    return undefined;
}


const getParsedFilterValue = (value) => {
    if (value == '(Blank)') {
        return "";
    };
    return value;
}

export const getFilterValues = (filters = []) => {
    return filters.map(filter => {
        return {
            [filter.columnName]: getParsedFilterValue(filter.value)
        }
    }).reduce((acc, curr) => {
        return {
            ...acc,
            ...curr
        }
    }, {});
}

const getLocationOptions = (locations) => {
    const locationOptions = [];
    const facilitiesSorted = locations.filter(item => item.locationTypeId === 3).sort((a, b) => a.locationName.localeCompare(b.locationName));
    facilitiesSorted.forEach((facility, index) => {
        locationOptions.push(facility); //DUMPIN FACILITY INTO LOCATION OPTIONS
        const HUs = locations.filter(item => item.parentLocationId === facility.locationId && item.locationTypeId === 15).sort((a, b) => a.locationName.localeCompare(b.locationName));
        locationOptions.push(...HUs);
    });
    return locationOptions.map(item => item.locationName);
};

export const getAnnouncementAuditColumns = (locations) => {
    return [
        {
            name: 'sender',
            preset: 'IconAndTextCell',
            displayName: 'Sender',
            sortOrder: 'none',
            isFilterable: true,
            isSortable: false,
            isVisible: true,
            type: 'preDefined',
            sortByProp: 'text'
        },
        {
            name: 'recipient',
            displayName: 'Recipient(s)',
            sortOrder: 'none',
            isFilterable: true,
            isSortable: false,
            isVisible: true,
            type: 'value',
            sortByProp: 'text'
        },
        {
            name: 'createdAt',
            displayName: 'Date',
            sortOrder: 'desc',
            isFilterable: true,
            isSortable: true,
            isVisible: true,
            type: 'value',
            sortByProp: 'createdAt'
        },
        {
            name: 'isDeleted',
            preset: 'IconAndTextCell',
            displayName: 'Status',
            sortOrder: 'none',
            isFilterable: true,
            isSortable: false,
            isVisible: true,
            type: 'preDefined',
            sortByProp: 'isDeleted',
            filter: {
                type: 'MultiTagCell'
            },
            options: ["Sent", "Deleted"]
        },
        {
            name: 'subject',
            displayName: 'Title',
            sortOrder: 'none',
            isFilterable: true,
            isSortable: false,
            isVisible: true,
            type: 'html',
        },
        {
            name: 'location',
            displayName: 'Locations',
            sortOrder: 'none',
            isFilterable: true,
            isSortable: false,
            isVisible: true,
            hidden: true,
            filter: {
                type: 'MultiTagCell'
            },
            options: getLocationOptions(locations)
        },
    ]
};


export const getConversationAuditColumns = (locations) => {
    return [
        {
            name: 'sender',
            preset: 'IconAndTextCell',
            displayName: 'Sender',
            sortOrder: 'none',
            isFilterable: true,
            isSortable: false,
            isVisible: true,
            type: 'preDefined',
            sortByProp: 'text'
        },
        {
            name: 'recipient',
            displayName: 'Recipient(s)',
            sortOrder: 'none',
            isFilterable: true,
            isSortable: false,
            isVisible: true,
            type: 'value',
            sortByProp: 'text'
        },
        {
            name: 'createdAt',
            displayName: 'Date',
            sortOrder: 'desc',
            isFilterable: true,
            isSortable: true,
            isVisible: true,
            type: 'value',
            sortByProp: 'createdAt'
        },
        {
            name: 'isDeleted',
            preset: 'IconAndTextCell',
            displayName: 'Status',
            sortOrder: 'none',
            isFilterable: true,
            isSortable: false,
            isVisible: true,
            type: 'preDefined',
            sortByProp: 'isDeleted',
            filter: {
                type: 'MultiTagCell'
            },
            options: ["Sent", "Deleted"]
        },
        {
            name: 'message',
            displayName: 'Message Preview',
            sortOrder: 'none',
            isFilterable: true,
            isSortable: false,
            isVisible: true,
            type: 'html',
        },
        {
            name: 'location',
            displayName: 'Locations',
            sortOrder: 'none',
            isFilterable: true,
            isSortable: false,
            isVisible: true,
            hidden: true,
            filter: {
                type: 'MultiTagCell'
            },
            options: getLocationOptions(locations)
        },
    ]
};

const highlightSearchString = (string = "", searchString) => {
    return string.replace(new RegExp(searchString, 'gi'), (match) => `<b>${match}</b>`);
};

export const parseAuditLogResponse = (response, filters) => {
    const filter = getFilter(filters, 'subject');
    const searchString = filter ? filter.value : '';
    response.data.content = response.data.content.map((_data) => {
        _data.subject = highlightSearchString(_data.subject, searchString);
        return _data;
    });
    return response;
}


export const parseConversationAuditLogResponse = (response, filters) => {
    const filter = getFilter(filters, "message");
    if (filter) {
        const searchString = filter ? filter.value : '';
        response.data.content = response.data.content.map((_data) => {
            _data.message = highlightSearchString(_data.message, searchString);
            return _data;
        });
    }
    return response;
}

export const getIsDeletedFilter = (filters) => {
    const filter = getFilter(filters, 'isDeleted');
    if (filter) {
        if (filter.value.indexOf("Sent") > -1 && filter.value.indexOf("Deleted") > -1) {
            return undefined;
        } else if (filter.value.indexOf("Sent") > -1) {
            return false;
        } else if (filter.value.indexOf("Deleted") > -1) {
            return true;
        }
    }
    return undefined;
}

/**
 * Normalizes audit dependencies for comparison
 * Creates a serializable object from audit hook dependencies
 * @param {number} currentPage - Current page number
 * @param {number} pageSize - Page size
 * @param {string} sortColumn - Column to sort by
 * @param {string} sortDirection - Sort direction ('asc' or 'desc')
 * @param {string} selectedType - Selected audit type
 * @param {Array} filter - Filter array
 * @returns {Object} Normalized dependency object
 */
export const normalizeAuditDependencies = (currentPage, pageSize, sortColumn, sortDirection, selectedType, filter) => {
    return {
        currentPage,
        pageSize,
        sortColumn,
        sortDirection,
        selectedType,
        filterLength: filter.length,
        filterString: JSON.stringify(filter)
    };
}

/**
 * Checks if audit dependencies have changed
 * Compares current dependencies with previous dependencies
 * @param {Object} currentDeps - Current normalized dependencies
 * @param {Object} prevDeps - Previous normalized dependencies (can be empty object)
 * @returns {boolean} True if dependencies changed, false otherwise
 */
export const hasAuditDependenciesChanged = (currentDeps, prevDeps) => {
    const currentDepsString = JSON.stringify(currentDeps);
    const prevDepsString = JSON.stringify(prevDeps || {});
    return currentDepsString !== prevDepsString;
}

export const updateConversationAuditLogResponse = (response) => {
    response.data.content = response.data.content.map((_data) => {
        return {
            senderName: _data.sender.name,
            recipient: _data.recipients.map(item => ({name: item})),
            createdAt: _data.dateSent,
            isDeleted: _data.status == 'DELETED',
            message: _data.messagePreview,
            feedId: _data.feedId,
            id: _data.messageId,
            isShutdown: _data.isShutdown,
            roleName: _data.sender.roleName
        };
    });
    return response;
}
